/**
 * Eck Design Add-ons v3.2.
 * Selected-state UI, estimated subtotal and static shipping note only.
 * The existing Horizon form/cart submission stays untouched.
 */
class EckProductAddons extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    try {
      this._variantPrices = JSON.parse(this.querySelector('[data-eck-main-variant-prices]')?.textContent || '{}');
    } catch {
      this._variantPrices = {};
    }
    try {
      this._variantMeta = JSON.parse(this.querySelector('[data-eck-main-variant-meta]')?.textContent || '{}');
    } catch {
      this._variantMeta = {};
    }

    this._scope = this.closest('.product-details') || this.closest('product-form-component') || this.parentElement;
    this._onScopeEvent = (event) => {
      if (event.target instanceof Element && this.contains(event.target)) return;
      this.scheduleUpdate();
    };
    this._scope?.addEventListener('input', this._onScopeEvent);
    this._scope?.addEventListener('change', this._onScopeEvent);
    this._scope?.addEventListener('click', this._onScopeEvent);

    // Horizon may update the selected variant asynchronously via section rendering.
    this._observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => {
        const el = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        return el && !this.contains(el) && !el.closest?.('[data-eck-cart-price]');
      })) this.scheduleUpdate();
    });
    if (this._scope) this._observer.observe(this._scope, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'selected']
    });

    this.addEventListener('click', (event) => {
      const control = event.target.closest('[data-eck-add], [data-eck-step]');
      if (!control || !this.contains(control)) return;
      const row = control.closest('[data-eck-addon-row]');
      const input = row?.querySelector('[data-eck-addon-quantity]');
      if (!input) return;
      const current = this.normalizeQuantity(input.value);
      const next = control.matches('[data-eck-add]') ? Math.max(1, current)
        : this.normalizeQuantity(current + Number(control.dataset.eckStep || 0));
      input.value = String(next);
      this.updateSummary();
      if (control.matches('[data-eck-add]')) row.querySelector('[data-eck-step="1"]')?.focus();
      else if (next === 0) row.querySelector('[data-eck-add]')?.focus();
    });

    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-eck-addon-quantity]')) this.updateSummary();
    });
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-eck-addon-variant]')) {
        const row = target.closest('[data-eck-addon-row]');
        const selected = target.selectedOptions[0];
        if (row && selected) {
          row.dataset.variantId = selected.value;
          row.dataset.price = selected.dataset.price || '0';
          const label = row.querySelector('[data-eck-price-display]');
          if (label) label.textContent = selected.dataset.priceFormatted || '';
        }
      }
      if (target.matches('[data-eck-addon-quantity]')) target.value = String(this.normalizeQuantity(target.value));
      if (target.matches('[data-eck-addon-variant], [data-eck-addon-quantity]')) this.updateSummary();
    });
    this.renameMainQuantityHeading();
    this.updateSummary();
  }

  // Presentation only. Do not move or replace Horizon's native quantity selector.
  renameMainQuantityHeading() {
    const wrapper = this.closest('.product-form-buttons--eck-addons');
    const label = wrapper?.querySelector('.eck-main-quantity-heading');
    if (label && label.textContent.trim().toLowerCase() === 'main product quantity') {
      label.textContent = 'Quantity';
    }
  }

  disconnectedCallback() {
    this._scope?.removeEventListener('input', this._onScopeEvent);
    this._scope?.removeEventListener('change', this._onScopeEvent);
    this._scope?.removeEventListener('click', this._onScopeEvent);
    this._observer?.disconnect();
    if (this._frame) cancelAnimationFrame(this._frame);
    this._initialized = false;
  }

  scheduleUpdate() {
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      if (this.isConnected) {
        this.renameMainQuantityHeading();
        this.updateSummary();
      }
    });
  }

  normalizeQuantity(value) {
    const number = Number.parseInt(value, 10);
    return Number.isFinite(number) ? Math.max(0, Math.min(99, number)) : 0;
  }

  money(minorUnits) {
    const currency = this.dataset.currency || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency
      }).format(minorUnits / 100);
    } catch {
      return `${(minorUnits / 100).toFixed(2)} ${currency}`;
    }
  }

  getMainQuantity() {
    const root = this.closest('.product-form-buttons--eck-addons') || this._scope || this.parentElement;
    const candidates = [
      'quantity-selector-component input[name="quantity"]',
      'quantity-selector-component input[type="number"]',
      'input[name="quantity"]'
    ];
    for (const selector of candidates) {
      const input = root.querySelector(selector);
      if (input && !this.contains(input)) {
        const q = Number.parseInt(input.value, 10);
        if (Number.isFinite(q) && q > 0) return q;
      }
    }
    return 1;
  }

  getMainVariantId() {
    const scope = this._scope || this.closest('.product-details') || document;
    const form = scope.querySelector('product-form-component form, form[data-type="add-to-cart-form"]');
    const variantInput = form?.querySelector('input[name="id"]');
    return variantInput?.value || this.dataset.mainVariant;
  }

  getMainPrice() {
    const scope = this._scope || this.closest('.product-details') || document;
    const form = scope.querySelector('product-form-component form, form[data-type="add-to-cart-form"]');
    const variantInput = form?.querySelector('input[name="id"]');
    const variantId = this.getMainVariantId();
    if (Object.prototype.hasOwnProperty.call(this._variantPrices, variantId)) {
      return Number(this._variantPrices[variantId]);
    }
    // Unknown new variant: don't show an incorrect subtotal.
    if (variantInput?.value && variantInput.value !== this.dataset.mainVariant) return null;
    const initial = Number(this.dataset.mainPrice);
    return Number.isFinite(initial) ? initial : null;
  }

  addSummaryLine(container, name, money, imageUrl) {
    const line = document.createElement('div');
    line.className = 'eck-order__line eck-order__addon';
    const label = document.createElement('span');
    label.className = 'eck-order__line-name';
    label.textContent = name;
    const item = document.createElement('span');
    item.className = 'eck-order__item';
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'eck-order__thumb';
      img.src = imageUrl;
      img.alt = '';
      img.width = 38;
      img.height = 38;
      img.loading = 'lazy';
      item.append(img);
    }
    item.append(label);
    const amount = document.createElement('span');
    amount.className = 'eck-order__line-price';
    amount.textContent = this.money(money);
    line.append(item, amount);
    container.append(line);
  }

  // Show an estimated subtotal on the existing Horizon Add to Cart label only when
  // the known text wrapper is present. Never overwrite button HTML or form behavior.
  updateCartButtonEstimate(total) {
    const wrapper = this.closest('.product-form-buttons--eck-addons');
    // Do not target Horizon's separate sticky add-to-cart button.
    const host = wrapper?.querySelector('add-to-cart-component');
    const button = host?.querySelector('button.add-to-cart-button, button[type="submit"]');
    if (!button) return;
    // Horizon builds vary: use a text-only wrapper when available; never replace
    // button markup or event handlers (which also control your custom add-ons).
    const textLabel = button.querySelector('.add-to-cart-text__content > span:first-child')
      || button.querySelector('.add-to-cart-text__content')
      || button.querySelector('.add-to-cart-button__text')
      || button.querySelector('[data-add-to-cart-text]');
    if (!textLabel) return;
    let amount = textLabel.querySelector('[data-eck-cart-price]');
    if (!amount) {
      amount = document.createElement('span');
      amount.dataset.eckCartPrice = '';
      amount.className = 'eck-addons__cart-total';
      textLabel.append(amount);
    }
    amount.textContent = !button.disabled && total !== null ? ` — ${this.money(total)}` : '';
  }

  updateSummary() {
    let addonTotal = 0;
    const chosen = [];
    for (const row of this.querySelectorAll('[data-eck-addon-row]')) {
      const input = row.querySelector('[data-eck-addon-quantity]');
      if (!input) continue;
      const qty = this.normalizeQuantity(input.value);
      const addonPrice = Number(row.dataset.price) || 0;
      const selected = qty > 0;
      if (selected) {
        addonTotal += qty * addonPrice;
        chosen.push({
          title: row.dataset.addonName || row.querySelector('.eck-addons__name')?.textContent?.trim() || 'Add-on',
          quantity: qty,
          amount: qty * addonPrice,
          imageUrl: row.querySelector('.eck-addons__image')?.getAttribute('src') || ''
        });
      }
      row.classList.toggle('is-selected', selected);
      const add = row.querySelector('[data-eck-add]');
      const quantityControl = row.querySelector('[data-eck-qty-control]');
      const addedLabel = row.querySelector('[data-eck-added]');
      if (add) add.hidden = selected;
      if (quantityControl) quantityControl.hidden = !selected;
      if (addedLabel) addedLabel.hidden = !selected;
    }

    const count = this.getMainQuantity();
    const unitPrice = this.getMainPrice();
    const total = unitPrice === null ? null : count * unitPrice + addonTotal;
    const mainLine = this.querySelector('[data-eck-main-line]');
    const mainAmount = this.querySelector('[data-eck-main-amount]');
    const meta = this._variantMeta?.[this.getMainVariantId()] || {};
    const variantTitle = meta.title && meta.title !== 'Default Title' ? ` (${meta.title})` : '';
    if (mainLine) mainLine.textContent = `${this.dataset.mainTitle || 'Main product'}${variantTitle} × ${count}`;
    const mainImage = this.querySelector('[data-eck-main-image]');
    if (mainImage && meta.image && mainImage.getAttribute('src') !== meta.image) {
      mainImage.setAttribute('src', meta.image);
      mainImage.removeAttribute('srcset');
    }
    if (mainAmount) mainAmount.textContent = unitPrice === null ? '—' : this.money(unitPrice * count);

    const addonLines = this.querySelector('[data-eck-order-addon-lines]');
    if (addonLines) {
      addonLines.replaceChildren();
      for (const item of chosen) this.addSummaryLine(addonLines, `${item.title} × ${item.quantity}`, item.amount, item.imageUrl);
    }
    const subtotalDisplay = this.querySelector('[data-eck-subtotal]');
    if (subtotalDisplay) subtotalDisplay.textContent = total === null ? '—' : this.money(total);

    this.updateCartButtonEstimate(total);
    const error = this.querySelector('[data-eck-error]');
    if (error) error.hidden = true;
  }
}
if (!customElements.get('eck-product-addons')) customElements.define('eck-product-addons', EckProductAddons);
